import { create } from 'zustand';
import { getValidToken, isBffMode, isSessionAlive } from '@/features/auth';
import { aiApi, type AiHealth } from '../api/ai';

type AiStatus = 'unknown' | 'up' | 'down';

interface AiAvailabilityStore {
  status: AiStatus;
  load: AiHealth['load'] | null;
  problems: string[];
  available: boolean;
  setUp: (load: AiHealth['load'], problems?: string[]) => void;
  setDown: () => void;
}

export const useAiAvailabilityStore = create<AiAvailabilityStore>((set) => ({
  status: 'unknown',
  load: null,
  problems: [],
  available: false,
  setUp: (load, problems = []) => set({ status: 'up', available: true, load, problems }),
  setDown: () => set({ status: 'down', available: false, load: null, problems: [] }),
}));

let inFlight: Promise<void> | null = null;

const HEALTH_CACHE_KEY = 'ai_health_up';
const HEALTH_CACHE_TTL_MS = 60_000;

interface HealthCache {
  at: number;
  load: AiHealth['load'];
  problems: string[];
}

function readHealthCache(): HealthCache | null {
  try {
    const raw = localStorage.getItem(HEALTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HealthCache;
    if (Date.now() - parsed.at > HEALTH_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeHealthCache(load: AiHealth['load'], problems: string[]): void {
  try {
    localStorage.setItem(HEALTH_CACHE_KEY, JSON.stringify({ at: Date.now(), load, problems }));
  } catch {
  }
}

const HEALTH_TIMEOUT_MS = 10_000;

function probe(): Promise<void> {
  return getValidToken()
    .then((token) => {
      if (!token && !(isBffMode() && isSessionAlive())) {
        useAiAvailabilityStore.getState().setDown();
        return;
      }
      return aiApi.health().then((r) => {
        const { setUp, setDown } = useAiAvailabilityStore.getState();
        if (r.data.aiCore?.ok) {
          setUp(r.data.load, r.data.aiCore.problems);
          writeHealthCache(r.data.load, r.data.aiCore.problems ?? []);
        } else {
          setDown();
        }
      });
    })
    .catch(() => useAiAvailabilityStore.getState().setDown())
    .finally(() => {
      inFlight = null;
    });
}

export function ensureAiHealth(opts?: { refresh?: boolean }): Promise<void> {
  if (!inFlight) {
    const known = useAiAvailabilityStore.getState().status !== 'unknown';
    if (known && !opts?.refresh) return Promise.resolve();
    const cached = !opts?.refresh ? readHealthCache() : null;
    if (cached) {
      useAiAvailabilityStore.getState().setUp(cached.load, cached.problems);
      return Promise.resolve();
    }
    inFlight = probe();
  }
  return Promise.race([
    inFlight,
    new Promise<void>((resolve) => setTimeout(resolve, HEALTH_TIMEOUT_MS)),
  ]);
}
